import {
  FilterSet,
  FilterSetType,
  FilterSetLight,
} from "src/app/core/interfaces/filter-interface";
import { ContactCardInterface } from "src/app/core/interfaces/contact-card-interface";
import { MobilityContactCardInterface } from "src/app/core/interfaces/mobility-contact-card-interface";
import {
  signalStore,
  withState,
  withMethods,
  patchState
} from "@ngrx/signals";
import { MobilityRefService } from "src/app/features/services/mobility/mobility-ref-service";
import { inject } from "@angular/core";
import { FiltersService } from "src/app/core/services/filters/filters-service";
import {
  JobCardInterface,
  JobSearchInterface,
} from "src/app/core/interfaces/job-card-interface";
import { JobService } from "../services/mobility/job-service";
import { Pagination } from "src/app/core/interfaces/pagination";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { DetailedUserService } from "../services/user-profile/detailed-user-service";
import { JobSearchConverter } from "src/app/shared/utils/job-search-converter";
import {
  TileInterface,
  VideoTileDto,
} from "src/app/core/interfaces/tile-interface";

interface JourneySearchState {
  locationFilters: FilterSet;
  branchesFilters: FilterSet;
  activitiesFilters: FilterSet;
  locationFiltersSelected: FilterSet;
  branchesFiltersSelected: FilterSet;
  activitiesFiltersSelected: FilterSet;
  languageFilters: FilterSet;
  tilesProject: TileInterface[];
  tilesLanguageTest: TileInterface[];
  videos: VideoTileDto[];
  mobilityRefs: MobilityContactCardInterface[];
  jobs: JobCardInterface[];
  tilesOffer: TileInterface[];
  contactRhList: ContactCardInterface[];
  isJourneyImmature: boolean;
  loading: boolean;
  jobLoading: boolean;
  contactRhLoading: boolean;
  mobilityRefsLoading: boolean;
  videosPagination: Pagination;
  jobsPagination: Pagination;
  isRequestErrorModalOpen: boolean;
  tilesLoading: boolean;
}

const initialState: JourneySearchState = {
  locationFilters: null,
  branchesFilters: null,
  activitiesFilters: null,
  locationFiltersSelected: null,
  branchesFiltersSelected: null,
  activitiesFiltersSelected: null,
  languageFilters: null,
  tilesProject: [],
  tilesLanguageTest: [],
  videos: [],
  mobilityRefs: [],
  jobs: [],
  tilesOffer: [],
  contactRhList: [],
  contactRhLoading: false,
  isJourneyImmature: (() => {
    try {
      const saved = localStorage.getItem("isJourneyImmature");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  })(),
  loading: false,
  jobLoading: false,
  mobilityRefsLoading: false,
  videosPagination: null,
  jobsPagination: null,
  isRequestErrorModalOpen: false,
  tilesLoading: false,
};

import { secureShuffle } from "src/app/shared/utils/secure-random";

export const JourneySearchStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withMethods((store) => {
    const mobilityRefService = inject(MobilityRefService);
    const detailedUserService = inject(DetailedUserService);
    const jobService = inject(JobService);
    const tilesService = inject(TilesApiService);
    const filtersService = inject(FiltersService);

    return {
      setFilters(
        locationFilters,
        branchesFilters,
        activitiesFilters,
        languageFilters
      ): void {
        patchState(store, {
          locationFilters,
          branchesFilters,
          activitiesFilters,
          languageFilters,
        });
      },
      setFiltersSelected(
        locationFiltersSelected: FilterSet,
        branchesFiltersSelected: FilterSet,
        activitiesFiltersSelected: FilterSet
      ): void {
        patchState(store, {
          locationFiltersSelected,
          branchesFiltersSelected,
          activitiesFiltersSelected,
        });
      },
      setTilesProject(tilesProject: TileInterface[]) {
        patchState(store, { tilesProject });
      },
      setTilesLanguageTest(tilesLanguageTest: TileInterface[]) {
        patchState(store, { tilesLanguageTest });
      },
      setTilesOffer(tilesOffer: TileInterface[]) {
        patchState(store, { tilesOffer });
      },
      anyVideos(): boolean {
        return store.videos().length > 0;
      },
      setVideos(videos: VideoTileDto[]) {
        patchState(store, { videos });
      },
      setVideosPagination(videosPagination: Pagination) {
        patchState(store, { videosPagination });
      },
      setJobsPagination(jobsPagination: Pagination) {
        patchState(store, { jobsPagination });
      },
      setJobLoading(jobLoading: boolean) {
        patchState(store, { jobLoading });
      },
      setContactRhLoading(contactRhLoading: boolean) {
        patchState(store, { contactRhLoading });
      },
      setMobilityRefsLoading(mobilityRefsLoading: boolean) {
        patchState(store, { mobilityRefsLoading });
      },
      setIsRequestErrorModalOpen(isRequestErrorModalOpen: boolean) {
        patchState(store, { isRequestErrorModalOpen });
      },
      anyMobilityRefs(): boolean {
        return store.mobilityRefs().length > 0;
      },
      setMobilityRefs(mobilityRefs: MobilityContactCardInterface[]) {
        patchState(store, { mobilityRefs });
      },
      anyJobs(): boolean {
        return store.jobs().length > 0;
      },
      setJobs(jobs: JobCardInterface[]) {
        patchState(store, { jobs });
      },
      setContactRhList(contactRhList: ContactCardInterface[]): void {
        patchState(store, { contactRhList });
      },
      setLoading(loading: boolean): void {
        patchState(store, { loading });
      },
      setJourneyMaturity(isJourneyImmature: boolean): void {
        localStorage.setItem(
          "isJourneyImmature",
          JSON.stringify(isJourneyImmature)
        );
        patchState(store, { isJourneyImmature });
      },
      setTilesLoading(tilesLoading: boolean): void {
        patchState(store, { tilesLoading });
      },
      loadJobErrorManagement(error) {
        if (error.status === 400) {
          this.setIsRequestErrorModalOpen(true);
        }
      },
      async loadMoreJobs(): Promise<void> {
        const currentJobsPagination = store.jobsPagination();
        const selectedFilters = [
          store.activitiesFiltersSelected(),
          store.branchesFiltersSelected(),
          store.locationFiltersSelected(),
        ].filter((filterSet): filterSet is FilterSet => !!filterSet);
        try {
          const jobsSearchData: JobSearchInterface = {
            selectedFilters,
            locationSearchValue: null,
            pagination: currentJobsPagination,
            useCustomProperties: true,
          };

          const jobsResult = await jobService.getJobsListForJourney(
            JobSearchConverter.toLightweight(jobsSearchData)
          );

          this.setJobs([...store.jobs(), ...jobsResult.jobCards]);
        } catch (error) {
          this.loadJobErrorManagement(error);
        }
      },
      async loadVideos(): Promise<void> {
        const selectedFilters = [
          store.activitiesFiltersSelected(),
          store.branchesFiltersSelected(),
        ];

        const currentPagination = store.videosPagination();
        try {
          const videoResult = await tilesService.getVideoResult(
            selectedFilters,
            currentPagination
          );
          const videoTiles = videoResult.videoTiles;

          this.setVideosPagination(videoResult.pagination);
          this.setVideos(videoTiles);
        } catch (error) {
          this.setVideos([]);
        } finally {
        }
      },
      async loadMoreVideos(): Promise<void> {
        const videoTiles: VideoTileDto[] = await this.getVideoTiles();
        this.setVideos([...store.videos(), ...videoTiles]);
      },
      async loadMobilityRefs(): Promise<void> {
        const selectedFilters = [
          store.activitiesFiltersSelected(),
          store.locationFiltersSelected(),
        ].filter((filterSet): filterSet is FilterSet => !!filterSet);

        const toFilterInterfaceLight = (
          filter: FilterSet["filters"][number]
        ): FilterSetLight["filters"][number] => ({
          spId: filter.spId,
          customProperties: filter.customProperties,
          filters: filter.filters?.map(toFilterInterfaceLight),
        });

        const selectedFiltersLight: FilterSetLight[] = selectedFilters.map(
          (filterSet) => ({
            spId: filterSet.spId,
            filterSetType: filterSet.filterSetType,
            filters: filterSet.filters?.map(toFilterInterfaceLight),
            filtersCategories: filterSet.filtersCategories?.map(
              toFilterInterfaceLight
            ),
          })
        );
        try {
          this.setMobilityRefsLoading(true);
          const mobilityRefContacts =
            await mobilityRefService.getMobilityRefsForJourney(
              selectedFiltersLight
            );
          const shuffledMobilityRefContacts =
            secureShuffle(mobilityRefContacts);
          this.setMobilityRefs(shuffledMobilityRefContacts);
        } catch (_error) {
          this.setMobilityRefs([]);
        } finally {
          this.setMobilityRefsLoading(false);
        }
      },
      async loadTilesByTheme(): Promise<void> {
        this.setTilesLoading(true);
        try {
          const rawTiles = await tilesService.getTilesJourney(
            store.isJourneyImmature()
          );
          const allTiles = rawTiles ?? [];
          const projectClarificationThemeName = "Clarifier mon projet";
          const offerDiscoveryThemeName = "Découvrir les offres";
          const languageTestThemeName = "Test de langue";

          const tilesProject: TileInterface[] = allTiles
            .filter(
              (tile) =>
                tile.personalizedJourneyTheme ===
                  projectClarificationThemeName ||
                tile.personalizedJourneyTheme === null
            )
            .map((t, idx) => tilesService.mapTileToTileInterface(t, idx));

          this.setTilesProject(tilesProject);

          if (!store.isJourneyImmature()) {
            const tilesOffer: TileInterface[] = allTiles
              .filter(
                (tile) =>
                  tile.personalizedJourneyTheme === offerDiscoveryThemeName
              )
              .map((t, idx) => tilesService.mapTileToTileInterface(t, idx));

            this.setTilesOffer(tilesOffer);

            const tilesLanguageTest: TileInterface[] = allTiles
              .filter(
                (tile) =>
                  tile.personalizedJourneyTheme === languageTestThemeName
              )
              .map((t, idx) => tilesService.mapTileToTileInterface(t, idx));

            this.setTilesLanguageTest(tilesLanguageTest);
          }
        } finally {
          this.setTilesLoading(false);
        }
      },
      async loadJobs(): Promise<void> {
        const selectedFilters = [
          store.activitiesFiltersSelected(),
          store.branchesFiltersSelected(),
          store.locationFiltersSelected(),
        ].filter((filterSet): filterSet is FilterSet => !!filterSet);
        const currentJobsPagination = store.jobsPagination();
        try {
          this.setJobLoading(true);

          const jobsSearchData: JobSearchInterface = {
            selectedFilters,
            locationSearchValue: null,
            pagination: currentJobsPagination,
            useCustomProperties: true,
          };

          const jobResults = await jobService.getJobsListForJourney(
            JobSearchConverter.toLightweight(jobsSearchData)
          );

          this.setJobs(jobResults.jobCards);
          this.setJobsPagination(jobResults.pagination);
        } catch (error) {
          this.loadJobErrorManagement(error);
        } finally {
          this.setJobLoading(false);
        }
      },

      async loadHrContact(): Promise<void> {
        try {
          this.setContactRhLoading(true);
          const contactRh = await detailedUserService.getContactRh();
          this.setContactRhList(contactRh);
        } finally {
          this.setContactRhLoading(false);
        }
      },
      async loadJourneyFilters(): Promise<void> {
        const filterGroup = await filtersService.getJourneyFilterGroup();

        const filterSetGeographicalLocation = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.GeographicalLocation
        );
        const filterSetSector = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Sector
        );
        const filterSetActivity = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Activity
        );
        const filterSetLanguage = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Language
        );

        filterSetGeographicalLocation.filtersCategories =
          filtersService.getTopLevelsGeographicalLocationFilterCategories(
            filterSetGeographicalLocation.filters
          );
        filterSetSector.filtersCategories =
          filtersService.getAllFilterCategories(filterSetSector.filters);
        filterSetActivity.filtersCategories =
          filtersService.getAllFilterCategories(filterSetActivity.filters);

        filterSetGeographicalLocation.filters =
          filterSetGeographicalLocation.filters.filter(
            (x) => x.filters === null
          );
        filterSetSector.filters = filterSetSector.filters.filter(
          (x) => x.filters === null
        );
        filterSetActivity.filters = filterSetActivity.filters.filter(
          (x) => x.filters === null
        );

        this.setFilters(
          filterSetGeographicalLocation,
          filterSetSector,
          filterSetActivity,
          filterSetLanguage
        );
      },
      updateSelectedFiltersTranslations(): void {
        const locationFilters = store.locationFilters();
        const selectedLocationFilters = store.locationFiltersSelected();
        const branchesFilters = store.branchesFilters();
        const selectedBranchesFilters = store.branchesFiltersSelected();
        const activitiesFilters = store.activitiesFilters();
        const selectedActivitiesFilters = store.activitiesFiltersSelected();

        this.setFiltersSelected(
          selectedLocationFilters && locationFilters
            ? filtersService.updateFilterSetTranslations(
                selectedLocationFilters,
                locationFilters
              )
            : selectedLocationFilters,
          selectedBranchesFilters && branchesFilters
            ? filtersService.updateFilterSetTranslations(
                selectedBranchesFilters,
                branchesFilters
              )
            : selectedBranchesFilters,
          selectedActivitiesFilters && activitiesFilters
            ? filtersService.updateFilterSetTranslations(
                selectedActivitiesFilters,
                activitiesFilters
              )
            : selectedActivitiesFilters
        );
      },
    };
  })
);
